import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["currency", "formatter", "formatNegativeValuesWithParenthesis", "isNativeCurrency", "parser", "precision", "inputRef", "value", "error", "max", "readOnly", "min", "maxFractionDigits", "onBlur", "onChange", "onFocus", "onRawValueChange", "onKeyUp", "onKeyDown", "separator", "use", "disabled", "placeholder", "styled", "autoFocus", "disableFocusStyles"];
import { forwardRef, memo } from 'react';
import I18n from 'I18n';
import { formatIntlCurrency } from 'I18n/utils/formatIntlCurrency';
import { parseNumber } from 'I18n/utils/internal/light/numberFormatters';
import memoizeOne from 'react-utils/memoizeOne';
import { TextInput } from '../TextInput/TextInput';
import { Controllable } from '../Controllable';
import ShareInput from '../ShareInput/ShareInput';
import { AbstractNumberInput } from '../AbstractNumberInput/AbstractNumberInput';
import { filterProps } from '../../../utils/filterProps';
import { useDisableFocusStyles } from '../../../hooks/useDisableFocusStyles';
import { jsx as _jsx } from "react/jsx-runtime";
const BLANK_REGEX = /^\s*$/;
const formatUnsupportedCurrency = (currency, value, formatNegativeValuesWithParenthesis) => {
  if (formatNegativeValuesWithParenthesis) {
    if (value < 0) {
      return `(${currency} ${-value})`;
    }
  }
  return `${currency} ${value}`;
};
const getCurrencySymbol = (currency, isNativeCurrency) => {
  const symbolData = I18n.currencySymbols[currency];
  if (!symbolData) return currency;
  return (isNativeCurrency ? symbolData.symbol_native : symbolData.symbol) || currency;
};
const getCurrencyPrecision = (currency, value) => {
  const symbolData = I18n.currencySymbols[currency];
  if (!symbolData) {
    return 2;
  }
  return symbolData.alternative_decimal_digits && value % 1 !== 0 ? symbolData.alternative_decimal_digits : symbolData.decimal_digits;
};
const getCurrencyFormatter = (currency, isNativeCurrency, formatNegativeValuesWithParenthesis) => (value, {
  maxFractionDigits,
  precision
} = {}) => {
  if (value == null) return ''; // Assuming returning an empty string is acceptable

  const computedPrecision = precision || getCurrencyPrecision(currency, value);
  let formattedCurrency;
  try {
    formattedCurrency = formatIntlCurrency(value, {
      currencyCode: currency,
      currencySign: formatNegativeValuesWithParenthesis ? 'accounting' : 'standard',
      precision: maxFractionDigits ? Math.max(computedPrecision, maxFractionDigits) : computedPrecision,
      parts: {
        currency: getCurrencySymbol(currency, isNativeCurrency),
        literal: currency === 'CLP' ? '' : undefined // Handling specific currency
      }
    });
  } catch (e) {
    formattedCurrency = formatUnsupportedCurrency(currency, value, formatNegativeValuesWithParenthesis);
  }
  return formattedCurrency;
};
const getCurrencyParser = (currency, isNativeCurrency) => {
  return rawString => {
    const currencySymbol = getCurrencySymbol(currency, isNativeCurrency);
    const currencySymbolRegex = new RegExp(`${currencySymbol.replace(/\W/g, '\\$&')}`, 'g');
    const valueToParse = rawString.replace(currencySymbolRegex, '');
    return BLANK_REGEX.test(valueToParse) ? null : parseNumber(valueToParse);
  };
};
export const CurrencyInputComponent = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      currency,
      formatter,
      formatNegativeValuesWithParenthesis,
      isNativeCurrency,
      parser,
      precision,
      inputRef,
      value,
      error,
      max,
      readOnly,
      min,
      maxFractionDigits,
      onBlur,
      onChange,
      onFocus,
      onRawValueChange,
      onKeyUp,
      onKeyDown,
      separator,
      use = 'default',
      disabled,
      placeholder,
      styled = true,
      autoFocus,
      disableFocusStyles: disableFocusStylesProp
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const _getCurrencyFormatter = memoizeOne(getCurrencyFormatter);
  const _getCurrencyParser = memoizeOne(getCurrencyParser);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const disableFocusStyles = useDisableFocusStyles(disableFocusStylesProp);
  const inputProps = Object.assign({}, domProps, tokenProps, {
    placeholder,
    readOnly,
    inputRef: inputRef || ref,
    use,
    styled,
    autoFocus,
    disableFocusStyles
  });
  return /*#__PURE__*/_jsx(AbstractNumberInput, Object.assign({}, inputProps, {
    disabled: disabled,
    error: error,
    "aria-valuemax": max,
    "aria-valuemin": min,
    "aria-valuenow": value,
    max: max,
    min: min,
    maxFractionDigits: maxFractionDigits,
    onBlur: onBlur,
    onChange: onChange,
    onKeyDown: onKeyDown,
    onFocus: onFocus,
    onRawValueChange: onRawValueChange,
    onKeyUp: onKeyUp,
    separator: separator,
    value: value,
    formatter: formatter || currency && _getCurrencyFormatter(currency, isNativeCurrency, formatNegativeValuesWithParenthesis) || undefined,
    parser: parser || currency && _getCurrencyParser(currency, isNativeCurrency) || undefined,
    precision: precision,
    children: numberProps => /*#__PURE__*/_jsx(TextInput, Object.assign({}, numberProps, {
      type: "text"
    }))
  }));
});
export const CurrencyInput = ShareInput(Controllable( /*#__PURE__*/memo(CurrencyInputComponent)));
CurrencyInput.displayName = 'CurrencyInput';